import React from 'react';
import { FadeIn } from './animations/MotionPrimitives';
import SectionCourier from './illustrations/SectionCourier';
import './styles/Experience.css';

/*
 * Experience as a route of delivery stops rather than a pasted résumé.
 * The current engineering role leads with a verified impact strip and three
 * named impact blocks (shipped, investigated, strengthened); the other roles
 * stay compact on the same card surface. Every metric here is verified.
 */
const ROLES = [
  {
    role: 'Software Engineering Intern',
    org: 'International Farming Investment Management',
    period: 'Summer 2026',
    current: true,
    feature: true,
    impact: [
      { value: '25+', label: 'issues and support tickets' },
      { value: '20+', label: 'pull requests reviewed' },
      { value: '11', label: 'Cypress specifications' },
      { value: '2', label: 'platform releases' },
    ],
    blocks: [
      {
        title: 'Shipped production improvements',
        copy:
          'Built and delivered Angular and TypeScript fixes across FarmAdvisor and related workflows, resolving permission, routing, UI state, internationalization, data-export and workflow issues across more than 25 GitHub issues and Freshdesk tickets.',
      },
      {
        title: 'Investigated across the stack',
        copy:
          'Traced frontend and API behavior across GraphQL, REST and PostgreSQL-backed services, validated more than 20 pull requests and two releases, and helped resolve integration and regression issues before deployment.',
      },
      {
        title: 'Strengthened release confidence',
        copy:
          'Authored and refactored 11 Cypress end-to-end specifications using page objects, GraphQL-aware waits and non-destructive test data, while contributing regression plans and platform documentation.',
      },
    ],
    skills: ['Angular', 'TypeScript', 'GraphQL', 'REST APIs', 'Cypress', 'GitHub', 'Chrome DevTools'],
  },
  {
    role: 'Web Development Intern',
    org: 'City of Sacramento IT Department',
    period: 'Apr 2025 to Jul 2025',
    points: [
      'Built and improved public-facing city web experiences using Java, Apache Sling, JavaScript, CSS and Adobe Experience Manager, with a focus on accessibility, usability and page performance.',
      'Automated Formstack and Adobe Campaign workflows using APIs, conditional logic and integrations, reducing manual processing time by 60%.',
      'Investigated defects and supported release readiness across staging environments using Chrome DevTools, Jest, Jira, Elasticsearch and Docker-based workflows.',
    ],
    skills: ['Java', 'Adobe Experience Manager', 'JavaScript', 'Formstack', 'Jest', 'Docker'],
  },
  {
    role: 'Solutions Developer Intern',
    org: 'California Department of Toxic Substances Control',
    period: 'May 2024 to Mar 2025',
    points: [
      'Developed Python ETL pipelines that transformed and synchronized large statewide ArcGIS datasets into SQL Server, replacing manual data-handling steps with a repeatable workflow.',
      'Built geospatial analysis tools and interactive facility maps using ArcGIS Pro, Python APIs and spatial queries, improving workspace-utilization analysis by 90%.',
      'Integrated legislative, county and emergency-response data feeds into real-time ArcGIS dashboards, making operational information easier to explore and use.',
    ],
    skills: ['Python', 'ArcGIS', 'SQL Server', 'ETL', 'Geospatial'],
  },
  {
    role: 'Student Success Coach',
    org: 'Illinois Institute of Technology',
    period: 'Aug 2025 to Present',
    current: true,
    points: [
      'Mentor students through résumé reviews, interview preparation, career planning and structured follow-ups, helping them build clearer goals and stay accountable to their progress.',
      'Create HTML and CSS survey or outreach experiences for Illinois Tech’s 312 platform to collect student feedback and support career-program engagement.',
      'Design and send promotional email content and supporting Canva graphics for student programs, combining clear communication with accessible, visually organized outreach.',
    ],
    skills: ['Mentoring', 'Career Readiness', 'Communication', 'HTML', 'CSS', 'Canva'],
  },
];

function Experience() {
  return (
    <section className="section experience" id="experience">
      <div className="section-inner experience-inner">
        <FadeIn className="chapter-head experience-head">
          <span className="chapter-mark">02</span>
          <h2 className="chapter-title">Experience</h2>
        </FadeIn>

        <div className="timeline-wrap">
          {/* the courier arrives at the first stop on the route, ticket in claw */}
          <SectionCourier
            className="timeline-courier"
            carry="ticket"
            width={64}
            from={{ x: -16, y: -14, rotate: -14 }}
            restRotate={4}
          />

          <ol className="timeline">
            {ROLES.map((role, index) => (
              <FadeIn
                as="li"
                className={`timeline-item ${role.feature ? 'timeline-item--feature' : ''}`}
                key={`${role.org}-${role.role}`}
                delay={index * 0.05}
              >
                <span className="timeline-marker" aria-hidden="true" />

                <div className="timeline-card">
                  {role.feature && <span className="timeline-flag">Current Engineering Role</span>}

                  <div className="timeline-top">
                    <div className="timeline-heading">
                      <h3 className="timeline-role">{role.role}</h3>
                      <p className="timeline-org">{role.org}</p>
                    </div>
                    <span className={`timeline-period ${role.current ? 'is-current' : ''}`}>
                      {role.period}
                    </span>
                  </div>

                  {role.impact && (
                    <dl className="impact-strip">
                      {role.impact.map((stat) => (
                        <div className="impact-stat" key={stat.label}>
                          <dt className="impact-value">{stat.value}</dt>
                          <dd className="impact-label">{stat.label}</dd>
                        </div>
                      ))}
                    </dl>
                  )}

                  {role.blocks && (
                    <div className="impact-blocks">
                      {role.blocks.map((block) => (
                        <section className="impact-block" key={block.title}>
                          <h4 className="impact-block-title">{block.title}</h4>
                          <p className="impact-block-copy">{block.copy}</p>
                        </section>
                      ))}
                    </div>
                  )}

                  {role.points && (
                    <div className="role-points">
                      {role.points.map((point) => (
                        <p key={point}>{point}</p>
                      ))}
                    </div>
                  )}

                  <div className="tag-list timeline-tags">
                    {role.skills.map((skill) => (
                      <span className="tag" key={skill}>
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </FadeIn>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

export default Experience;
